import { useConfigContext } from '@/context/configContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { Button, Modal, Typography } from '@analytical-alley/ui';
import { useOptimizationSession } from '../../hooks/useOptimizationSession';

interface Props {
    onClose: () => void;
    channelName?: string;
    minChannelRecommended?: number;
    maxChannelRecommended?: number;
    customBody?: React.ReactNode;
}

export const OutOfBoundsWarningModal = ({
    onClose,
    channelName,
    minChannelRecommended,
    maxChannelRecommended,
    customBody,
}: Props) => {
    const {
        project: { projectLead },
    } = useDashboardGlobalContext();
    const {
        data: { optimized },
    } = useOptimizationSession();

    const { formatNumber } = useConfigContext();

    const showChannelRange =
        channelName !== undefined &&
        minChannelRecommended !== undefined &&
        maxChannelRecommended !== undefined;

    return (
        <Modal dismissible show onClose={onClose} popup size="lg">
            <Modal.Header>Whoa there! </Modal.Header>
            <Modal.Body>
                <div className="flex flex-col gap-4">
                    <img
                        src="/hold-your-horses-horse.gif"
                        alt="A horse on an emergency break"
                    />
                    {customBody ? (
                        customBody
                    ) : (
                        <>
                            <Typography variant="bodyM">
                                That&apos;s a big budget jump.
                            </Typography>
                            <Typography variant="bodyM">
                                Let&apos;s chat with your Customer Strategy
                                Manager <b>{projectLead.email}</b> before making
                                this change to make sure everything looks good.
                            </Typography>
                            {showChannelRange ? (
                                <Typography variant="bodyM">
                                    For automatic recommendations, the suggested
                                    budget range for <b>{channelName}</b> is
                                    from{' '}
                                    <b>{formatNumber(minChannelRecommended)}</b>{' '}
                                    to{' '}
                                    <b>{formatNumber(maxChannelRecommended)}</b>
                                    .
                                </Typography>
                            ) : (
                                <Typography variant="bodyM">
                                    The recommended budget change for an
                                    automatic recommendation is{' '}
                                    <b>
                                        from{' '}
                                        {formatNumber(
                                            optimized?.lowestBudget || 0,
                                        )}{' '}
                                        to{' '}
                                        {formatNumber(
                                            optimized?.highestBudget || 0,
                                        )}{' '}
                                    </b>
                                </Typography>
                            )}
                        </>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="py-4 gap-2">
                <Button size="sm" color="light" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
